import jwtDecode, { JwtPayload } from 'jwt-decode';
import { getToken, isAuthDisabled } from '../auth/UserService';
import { getLocalUser, getMsalUserRole } from '../auth/LocalAuthService';
import { RolesEnum } from '../constants/roles';
import { isDealRoomOnlyRole } from '../config/rolePermissions';

interface JwtPayloadUser extends JwtPayload {
    role?: string | number;
    Role?: string | number;
    roles?: Array<string | number>;
    companyExternalId?: string;
    sub?: string; // UserId
}

const ROLE_NAME_TO_ID: Record<string, number> = {
    member: RolesEnum.Member,
    admin: RolesEnum.Admin,
    manager: RolesEnum.Manager,
    superadmin: RolesEnum.SuperAdmin,
    'super admin': RolesEnum.SuperAdmin,
};

const toRoleId = (value: unknown): number | null => {
    if (typeof value === 'number' && !Number.isNaN(value)) {
        return value;
    }
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        const fromNumber = Number(trimmed);
        if (!Number.isNaN(fromNumber)) {
            return fromNumber;
        }
        const normalized = trimmed.toLowerCase().replace(/[_-]+/g, ' ');
        return ROLE_NAME_TO_ID[normalized] ?? null;
    }
    return null;
};

const getCurrentRoleId = (): number | null => {
    const token = getToken();
    if (!token) {
        const localAuthEnabled =
            process.env.REACT_APP_BYPASS_MSAL === 'true' ||
            process.env.NODE_ENV === 'development';
        if (localAuthEnabled) {
            const localUser = getLocalUser();
            if (localUser) {
                return Number(localUser.role);
            }
        }
        return null;
    }

    const decodedToken = jwtDecode<JwtPayloadUser>(token) as Record<string, unknown>;
    const directCandidates = [
        decodedToken.role,
        decodedToken.Role,
        decodedToken['extension_Role'],
        decodedToken['extension_role'],
        decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'],
    ];

    for (const candidate of directCandidates) {
        const roleId = toRoleId(candidate);
        if (roleId !== null) return roleId;
    }

    const roles = Array.isArray(decodedToken.roles) ? decodedToken.roles : [];
    for (const roleValue of roles) {
        const roleId = toRoleId(roleValue);
        if (roleId !== null) return roleId;
    }

    // B2C JWT has no role claim — fall back to role fetched from DB after login
    return getMsalUserRole();
};

const needsApprovalForBidSubmission = () => {
    var token = getToken();
    if (token) {
        const decodedToken = jwtDecode<JwtPayloadUser>(token);
        var role = decodedToken['role'];
        if (role) {
            return role === RolesEnum.Member.toString();
        }
    }
    return true;
};

const getCurrentRole = () => {
    const roleId = getCurrentRoleId();
    return roleId === null ? '' : String(roleId);
};

const getUserCompanyExternalId = () => {
    var token = getToken();
    if (token) {
        const decodedToken = jwtDecode<JwtPayloadUser>(token);
        return decodedToken.companyExternalId || '';
    } else {
        return '';
    }
}

const getUserId = () => {
    var token = getToken();
    if (token) {
        const decodedToken = jwtDecode<JwtPayloadUser>(token);
        return decodedToken.sub || '';
    } else {
        return '';
    }
}

const canViewAdminPanelMenu = () => {
    if (isAuthDisabled()) {
        return true;
    }

    const roleId = getCurrentRoleId();
    return roleId === RolesEnum.Admin || roleId === RolesEnum.SuperAdmin;
};

/** Azure AD / MSAL user with Member or Manager role (JWT `role` claim). Not used when local session exists. */
const isDealRoomOnlyMsalUser = () => {
    if (isAuthDisabled()) {
        return false;
    }

    const token = getToken();
    if (!token) {
        return false;
    }

    const roleId = getCurrentRoleId();
    if (roleId === null) {
        return false;
    }

    return isDealRoomOnlyRole(roleId);
};

const canAddNewCompany = () => {
    if (isAuthDisabled()) {
        return true;
    }

    // In local-auth / bypass-MSAL mode the page is already gated by AdminOnlyRoute.
    // Show the button for any authenticated local user regardless of DB role value.
    const localAuthEnabled =
        process.env.REACT_APP_BYPASS_MSAL === 'true' ||
        process.env.NODE_ENV === 'development';
    if (localAuthEnabled && getLocalUser() !== null) {
        return true;
    }

    return canViewAdminPanelMenu();
};

const PermissionsService = {
    needsApprovalForBidSubmission,
    getCurrentRole,
    getUserCompanyExternalId,
    canViewAdminPanelMenu,
    canAddNewCompany,
    getUserId,
    isDealRoomOnlyMsalUser,
};
export default PermissionsService;
